"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { trackEvent } from '../utils/analytics';

// We can keep a local reference if needed for any reason
const pageMetadata = {
  title: "Request a Quote | EMS Pulse",
  description: "Request a personalized quote for I-motion EMS technology for your fitness business."
};

export default function Contact() {
  // State for form fields - optimized state management
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    iMotionQuantity: 0,
    iModelQuantity: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  // State to track if component is mounted (for animations)
  const [isMounted, setIsMounted] = useState(false);

  // Handle form field changes with a single handler
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Effect to handle success animation
  useEffect(() => {
    if (submitStatus.success) {
      setShowSuccessAnimation(true);
      setShowConfetti(true);

      // Hide confetti after 4 seconds
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 4000);

      // Reset animation after display time
      const timer = setTimeout(() => {
        setShowSuccessAnimation(false);
      }, 5000); // 5 seconds duration for animation

      return () => {
        clearTimeout(timer);
        clearTimeout(confettiTimer);
      };
    }
  }, [submitStatus.success]);

  // Set mounted state after component loads
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Generate random confetti particles
  const generateConfetti = () => {
    const confetti = [];
    const colors = ["#38b5a0", "#f8c156", "#2389db", "#e05c88"]; // Core Values colors

    for (let i = 0; i < 50; i++) { // Reduced from 100 to 50 for better performance
      const left = Math.random() * 100; // Random horizontal position
      const width = Math.random() * 10 + 4; // Random size between 4-14px
      const height = width * (Math.random() * 0.8 + 0.2); // Random aspect ratio
      const color = colors[Math.floor(Math.random() * colors.length)]; // Random color
      const delay = Math.random() * 2; // Random delay
      const duration = Math.random() * 3 + 2; // Random duration between 2-5s

      confetti.push(
        <div
          key={i}
          className="absolute animate-confetti"
          style={{
            left: `${left}%`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: color,
            borderRadius: "2px",
            transform: `rotate(${Math.random() * 360}deg)`,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
          }}
        />,
      );
    }

    return confetti;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmitStatus({});

      // Send data to Formspree with plain text data
      const response = await fetch("https://formspree.io/f/mwpljzdr", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "Customer Name": formData.name,
          "Email Address": formData.email,
          "Phone Number": formData.phone,
          "I-Motion Units (Performance)": formData.iMotionQuantity,
          "I-Model Units (Aesthetic)": formData.iModelQuantity, 
          "Additional Information": formData.message || "No additional details provided",
          _subject: `Quote Request from ${formData.name}`,
          _format: "plain"
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Track the successful form submission
        trackEvent('form_submission', {
          form_name: 'quote_request',
          has_imotion: formData.iMotionQuantity > 0,
          has_imodel: formData.iModelQuantity > 0,
          total_quantity: formData.iMotionQuantity + formData.iModelQuantity
        });
        
        // Reset form on success
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: "",
          iMotionQuantity: 0,
          iModelQuantity: 0
        });
      } else {
        // Track the failed form submission
        trackEvent('form_error', {
          form_name: 'quote_request',
          error_message: result.message || 'Unknown error'
        });
      }

      // Set submission status for user feedback
      setSubmitStatus({
        success: response.ok,
        message: response.ok ? "Your quote request has been submitted successfully." : (result.message || "There was an error submitting your request. Please try again later."),
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Track the exception
      trackEvent('form_exception', {
        form_name: 'quote_request',
        error_message: error instanceof Error ? error.message : 'Unknown exception'
      });
      
      setSubmitStatus({
        success: false,
        message:
          "There was an error submitting your request. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle quantity adjustments
  const adjustQuantity = (system: "iMotionQuantity" | "iModelQuantity", amount: number) => {
    setFormData(prev => ({
      ...prev,
      [system]: Math.max(0, prev[system] + amount)
    }));
  };

  return (
    <div className="min-h-screen">
      {/* Confetti animation - only render when needed */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
          {generateConfetti()}
        </div>
      )}

      {/* Full-screen success animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/90 z-50 transition-all duration-700 backdrop-blur-sm">
          <div className="text-center flex flex-col items-center justify-center transform transition-all">
            {/* Success checkmark animation */}
            <div className="relative mb-4">
              <div className="w-24 h-24 rounded-full bg-[#38b5a0] flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  className="w-14 h-14"
                >
                  <path
                    fillRule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Animated rings */}
              <div className="absolute inset-0 rounded-full border-4 border-[#38b5a0] opacity-30"></div>
              <div className="absolute inset-0 rounded-full border-8 border-[#38b5a0]/20"></div>
            </div>

            <h2 className="text-3xl font-bold text-[#38b5a0] mb-2">
              Thank You!
            </h2>
            <p className="text-xl text-slate-700 max-w-md">
              Your quote request has been submitted successfully.
            </p>
            <p className="text-slate-600 mt-2">
              Our team will contact you shortly.
            </p>

            <button
              onClick={() => setShowSuccessAnimation(false)}
              className="mt-8 px-6 py-2 bg-[#287e6f] text-white rounded-lg hover:bg-[#1e665a] transition-colors shadow-lg shadow-[#287e6f]/20"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section 
        className="relative py-20 sm:py-24 md:py-28 px-4 bg-gradient-to-br from-[#1a2942] via-[#234563] to-[#1e5e52] text-white overflow-hidden min-h-[460px] !min-h-[460px] sm:min-h-0"
        style={{ minHeight: '460px' }}
      >
        {/* Abstract shapes with optimized animations */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-1/4 w-60 sm:w-80 md:w-96 h-60 sm:h-80 md:h-96 rounded-full bg-[#38b5a0] blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-1/5 w-60 sm:w-70 md:w-80 h-60 sm:h-70 md:h-80 rounded-full bg-[#f8c156] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-40 left-1/3 w-40 sm:w-50 md:w-60 h-40 sm:h-50 md:h-60 rounded-full bg-[#38b5a0]/40 blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span 
            className="inline-block bg-[#287e6f]/20 text-white text-lg mb-4 py-1 px-4 rounded-full border border-[#287e6f]/30 animate-[fadeSlideUp_0.7s_0.2s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Get in Touch
          </span>
          <h1 
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight animate-[fadeSlideUp_0.7s_0.4s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Request a Quote
          </h1>
          <p 
            className="text-xl opacity-90 max-w-3xl mx-auto animate-[fadeSlideUp_0.7s_0.6s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Ready to elevate your fitness business with I-motion EMS technology?
            Complete the form below to request your personalized quote.
          </p>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-[60px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </section>

      {/* Define the animations so they're available in this component */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Quote Form Section */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
            <div className="md:w-1/2 w-full">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-[#212529] relative">
                <span className="inline-block bg-[#38b5a0] h-2 w-20 absolute -top-4"></span>
                Start Your EMS Partnership
              </h2>
              <p className="text-base md:text-xl text-[#212529] mb-6 md:mb-8">
                As the official UK distributor for I-motion EMS systems, we
                provide complete guidance on system selection, implementation,
                and maximizing return on investment.
              </p>

              <div className="space-y-6 md:space-y-8 mb-8 md:mb-10">
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="bg-[#38b5a0]/10 p-3 md:p-4 rounded-lg text-[#38b5a0] mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 md:w-8 md:h-8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#212529] mb-1 md:mb-2">
                      Official UK Distributor
                    </h3>
                    <p className="text-base md:text-lg text-[#212529]">
                      Genuine I-motion technology with full manufacturer support
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-5">
                  <div className="bg-[#f8c156]/10 p-3 md:p-4 rounded-lg text-[#f8c156] mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 md:w-8 md:h-8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#212529] mb-1 md:mb-2">
                      Comprehensive Support
                    </h3>
                    <p className="text-base md:text-lg text-[#212529]">
                      Installation, training, and ongoing assistance included
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 md:gap-5">
                  <div className="bg-[#2389db]/10 p-3 md:p-4 rounded-lg text-[#2389db] mt-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-6 h-6 md:w-8 md:h-8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#212529] mb-1 md:mb-2">
                      Proven ROI
                    </h3>
                    <p className="text-base md:text-lg text-[#212529]">
                      Strategic investment with excellent returns for your
                      business.
                    </p>
                  </div>
                </div>
              </div>

              {/* Official UK Distributor - Optimized layout */}
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-md border border-[#DEE2E6] mb-6 md:mb-8">
                <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                  <div className="bg-[#38b5a0] p-2 md:p-3 rounded-full text-white shadow-md flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 md:w-7 md:h-7"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-[#212529]">
                    Official UK I-motion Distributor
                  </h3>
                </div>
                <p className="text-base text-[#212529]">
                  EMS Pulse is the exclusive official UK distributor for I-motion EMS technology. 
                  All systems include comprehensive installation, training, and ongoing UK-based support.
                </p>
              </div>

              {/* Contact Info Cards - Mobile-optimized layout */}
              <div className="grid grid-cols-1 gap-3 md:gap-4">
                {/* First row: Email and Phone */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  {/* Email Card */}
                  <div className="bg-white rounded-lg border border-[#DEE2E6] shadow-sm p-4 md:p-5 flex flex-col items-center">
                    <div className="bg-[#38b5a0]/10 p-2 md:p-3 rounded-full text-[#38b5a0] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 md:w-6 md:h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base md:text-lg font-bold mb-1 md:mb-2 text-[#212529]">Email</h3>
                    <p className="text-sm md:text-base text-[#212529]">sales@emspulse.co.uk</p>
                  </div>

                  {/* Phone Card */}
                  <div className="bg-white rounded-lg border border-[#DEE2E6] shadow-sm p-4 md:p-5 flex flex-col items-center">
                    <div className="bg-[#f8c156]/10 p-2 md:p-3 rounded-full text-[#f8c156] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 md:w-6 md:h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base md:text-lg font-bold mb-1 md:mb-2 text-[#212529]">Phone</h3>
                    <p className="text-sm md:text-base text-[#212529]">+44 (0) 77 9868 3345</p>
                  </div>
                </div>

                {/* Second row: Full-width Address */}
                <div className="bg-white rounded-lg border border-[#DEE2E6] shadow-sm p-4 md:p-5 flex flex-col items-center">
                  <div className="flex flex-col items-center mb-2 md:mb-4">
                    <div className="bg-[#2389db]/10 p-2 md:p-3 rounded-full text-[#2389db] w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5 md:w-6 md:h-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-[#212529]">Our Address</h3>
                  </div>
                  <div className="text-sm md:text-base text-[#212529] text-center">
                    <p className="mb-1">Furama House, Woodland Drive</p>
                    <p className="mb-1">Brighton, East Sussex</p>
                    <p className="mb-1">BN3 6DF</p>
                    <p>United Kingdom</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2 w-full">
              <div className="bg-white rounded-xl border border-[#DEE2E6] shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-[#212529]">
                  Request Your Quote
                </h3>

                {/* Form Status Message */}
                {submitStatus.message && (
                  <div
                    className={`mb-4 md:mb-6 p-3 md:p-4 rounded-lg ${
                      submitStatus.success
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    <div className="flex items-center">
                      {submitStatus.success ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 md:h-6 md:w-6 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.06 1.06l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 md:h-6 md:w-6 mr-2"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      <span className="text-base md:text-lg">{submitStatus.message}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4 md:space-y-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-base md:text-lg font-medium text-[#212529] mb-1 md:mb-2"
                      >
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-base md:text-lg border border-[#DEE2E6] rounded-lg focus:ring-2 focus:ring-[#38b5a0] focus:border-[#38b5a0] outline-none transition-colors"
                        placeholder="Full Name"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-base md:text-lg font-medium text-[#212529] mb-1 md:mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-base md:text-lg border border-[#DEE2E6] rounded-lg focus:ring-2 focus:ring-[#38b5a0] focus:border-[#38b5a0] outline-none transition-colors"
                        placeholder="you@example.com"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-base md:text-lg font-medium text-[#212529] mb-1 md:mb-2"
                      >
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 md:px-4 md:py-3 text-base md:text-lg border border-[#DEE2E6] rounded-lg focus:ring-2 focus:ring-[#38b5a0] focus:border-[#38b5a0] outline-none transition-colors"
                        placeholder="Your contact number"
                      />
                    </div>
                  </div>

                  {/* System Selection */}
                  <div className="pt-4 md:pt-5 border-t border-[#DEE2E6]">
                    <h4 className="text-lg md:text-xl font-medium text-[#212529] mb-3 md:mb-4">
                      System Interest
                    </h4>

                    <div className="space-y-3 md:space-y-4">
                      {/* I-motion System */}
                      <div className="p-4 md:p-5 rounded-lg border border-[#DEE2E6] bg-[#F8F9FA]">
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <div className="flex items-center gap-2 md:gap-3">
                            <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#2389db]"></span>
                            <h5 className="text-base md:text-lg font-medium text-[#212529]">
                              I-motion System{" "}
                              <span className="text-xs md:text-sm text-[#6C757D] font-normal">
                                (Performance)
                              </span>
                            </h5>
                          </div>
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={() => adjustQuantity("iMotionQuantity", -1)}
                              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-l-md bg-[#F8F9FA] hover:bg-[#DEE2E6] text-[#212529] text-lg md:text-xl"
                            >
                              -
                            </button>
                            <div className="w-10 h-8 md:w-12 md:h-10 flex items-center justify-center bg-white border-y border-[#DEE2E6] text-base md:text-lg">
                              {formData.iMotionQuantity}
                            </div>
                            <button
                              type="button"
                              onClick={() => adjustQuantity("iMotionQuantity", 1)}
                              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-r-md bg-[#F8F9FA] hover:bg-[#DEE2E6] text-[#212529] text-lg md:text-xl"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <p className="text-sm md:text-base text-[#212529]">
                          Professional performance EMS system with advanced
                          training capabilities.
                        </p>
                        <input type="hidden" name="iMotionQuantity" value={formData.iMotionQuantity} />
                      </div>

                      {/* I-model System */}
                      <div className="p-4 md:p-5 rounded-lg border border-[#DEE2E6] bg-[#F8F9FA]">
                        <div className="flex items-center justify-between mb-2 md:mb-3">
                          <div className="flex items-center gap-2 md:gap-3">
                            <span className="w-3 h-3 md:w-4 md:h-4 rounded-full bg-[#e979ab]"></span>
                            <h5 className="text-base md:text-lg font-medium text-[#212529]">
                              I-model System{" "}
                              <span className="text-xs md:text-sm text-[#6C757D] font-normal">
                                (Aesthetic)
                              </span>
                            </h5>
                          </div>
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={() => adjustQuantity("iModelQuantity", -1)}
                              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-l-md bg-[#F8F9FA] hover:bg-[#DEE2E6] text-[#212529] text-lg md:text-xl"
                            >
                              -
                            </button>
                            <div className="w-10 h-8 md:w-12 md:h-10 flex items-center justify-center bg-white border-y border-[#DEE2E6] text-base md:text-lg">
                              {formData.iModelQuantity}
                            </div>
                            <button
                              type="button"
                              onClick={() => adjustQuantity("iModelQuantity", 1)}
                              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-r-md bg-[#F8F9FA] hover:bg-[#DEE2E6] text-[#212529] text-lg md:text-xl"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <p className="text-sm md:text-base text-[#212529]">
                          Specialized aesthetic EMS system for body sculpting and
                          toning applications.
                        </p>
                        <input type="hidden" name="iModelQuantity" value={formData.iModelQuantity} />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-base md:text-lg font-medium text-[#212529] mb-1 md:mb-2"
                    >
                      Additional Information
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-3 py-2 md:px-4 md:py-3 text-base md:text-lg border border-[#DEE2E6] rounded-lg focus:ring-2 focus:ring-[#38b5a0] focus:border-[#38b5a0] outline-none transition-colors"
                      placeholder="Tell us about your business, specific requirements, or any questions you have."
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 md:py-4 px-4 md:px-6 rounded-lg bg-[#287e6f] hover:bg-[#1e665a] text-white text-lg md:text-xl font-medium shadow-lg shadow-[#287e6f]/20 transition-colors flex items-center justify-center ${
                      isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-5 w-5 md:h-6 md:w-6 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      "Submit Quote Request"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 px-4 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block bg-[#f8c156]/10 text-[#f8c156] font-medium px-4 py-1 md:px-5 md:py-2 rounded-full text-sm md:text-lg mb-3 md:mb-4">
              COMMON QUESTIONS
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-[#212529]">
              Frequently Asked Questions
            </h2>
            <p className="text-base md:text-xl text-[#212529] max-w-3xl mx-auto">
              Quick answers to questions we often receive about our I-motion EMS systems
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white p-5 md:p-8 rounded-xl shadow-md border border-[#DEE2E6]">
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-[#212529]">
                How quickly can I get set up with an I-motion system?
              </h3>
              <p className="text-base md:text-lg text-[#212529]">
                Once your order is confirmed, we typically deliver and install your I-motion 
                system within 2-3 weeks. Our comprehensive setup includes installation, staff 
                training, and business integration support.
              </p>
            </div>

            <div className="bg-white p-5 md:p-8 rounded-xl shadow-md border border-[#DEE2E6]">
              <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-[#212529]">
                What financing options are available?
              </h3>
              <p className="text-base md:text-lg text-[#212529]">
                We offer various financing solutions tailored to your business needs, including 
                lease options and payment plans. During your consultation, we can discuss the 
                financial approach that best aligns with your business model and goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Join the I-motion Revolution - Bottom CTA */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#38b5a0]/10 border-l-4 border-[#38b5a0] p-6 md:p-8 rounded-lg">
            <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-[#212529]">
              Join the I-motion Revolution
            </h3>
            <p className="text-base md:text-xl text-[#212529] mb-5 md:mb-6">
              Fitness businesses across the UK are already experiencing the
              benefits of I-motion EMS technology. Contact us today to discover
              how you can transform your business.
            </p>
            <Link
              href="/"
              className="inline-flex items-center bg-[#38b5a0] text-white px-5 py-3 md:px-6 md:py-4 text-base md:text-lg rounded-lg hover:bg-[#309289] transition-colors"
            >
              Learn More About I-motion
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4 md:w-5 md:h-5 ml-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
